import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AXIS, formatTooltipValue, iAggregateChartProps, timeLabel } from "./chartAxis";

/**
 * Aggregate throughput in and out over the window.
 *
 * `connectNulls` is false on purpose: a bucket no tenant measured is a gap,
 * and bridging it would draw a line through a time nobody reported on.
 */
function ThroughputChart({ data, width, height = 74 }: iAggregateChartProps) {
    if (data.points.length === 0) {
        return <div className="mp-chart-empty">No throughput reported in this window.</div>;
    }

    // Fed straight in, undownsampled — see chartAxis.ts for why.
    const points = data.points;

    const chart = (
        <LineChart
            data={points}
            width={width}
            height={height}
            margin={{ top: 4, right: 6, bottom: 0, left: 0 }}
        >
            <CartesianGrid stroke="#f2f2f2" />
            {/* type="number" + dataMin/dataMax preserves time-proportional spacing when the fan-out drops a bucket. */}
            <XAxis
                dataKey="at"
                type="number"
                domain={["dataMin", "dataMax"]}
                tickFormatter={timeLabel}
                {...AXIS}
            />
            <YAxis
                width={34}
                {...AXIS}
            />
            <Tooltip
                labelFormatter={label => timeLabel(Number(label))}
                formatter={formatTooltipValue}
            />
            {/*
                The legend is load-bearing, not decoration. It is the ONLY
                static colour key on the page: the sparkline column draws the
                same two series in the same two colours in a 20px cell with no
                room for a key of its own, and a Recharts hover tooltip cannot
                teach a mapping to someone scanning forty rows. Without this,
                blue-versus-green means nothing anywhere.
            */}
            <Legend
                verticalAlign="top"
                height={18}
                iconType="plainline"
                wrapperStyle={{ fontSize: 10 }}
            />
            <Line
                type="monotone"
                dataKey="received"
                name="In"
                stroke="#18a8d9"
                strokeWidth={1.5}
                dot={false}
                connectNulls={false}
            />
            <Line
                type="monotone"
                dataKey="sent"
                name="Out"
                stroke="#0b7a5e"
                strokeWidth={1.5}
                dot={false}
                connectNulls={false}
            />
        </LineChart>
    );

    if (width !== undefined) {
        return chart;
    }
    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            {chart}
        </ResponsiveContainer>
    );
}

export default ThroughputChart;
